import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import http from "../services/http";
import { dispatchStorageSync, replaceAuthSession } from "../services/sessionStorage";
import "../scss/login.scss";

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const formRef = useRef<HTMLFormElement>(null);
    const emailInputRef = useRef<HTMLInputElement>(null);
    const passwordInputRef = useRef<HTMLInputElement>(null);
    const autofillWatchRef = useRef<number | null>(null);
    const autofillStopTimerRef = useRef<number | null>(null);
    const [formData, setFormData] = useState({
        email: "",
        password: "",
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState("");
    const requestedPath = typeof location.state?.from === "string" && location.state.from.startsWith("/")
        ? location.state.from
        : "/";

    const hasValidCredentials = useCallback((values: { email: string; password: string }) => {
        const email = values.email.trim();
        return Boolean(
            email &&
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
            values.password
        );
    }, []);

    const readFormValues = useCallback(() => {
        const email = emailInputRef.current?.value;
        const password = passwordInputRef.current?.value;

        if (email !== undefined || password !== undefined) {
            return {
                email: email || "",
                password: password || "",
            };
        }

        const form = formRef.current;
        if (!form) return null;
        const data = new FormData(form);

        return {
            email: String(data.get("email") || ""),
            password: String(data.get("password") || ""),
        };
    }, []);

    const syncAutofillValues = useCallback(() => {
        const values = readFormValues();
        if (!values) return;

        setFormData(current => (
            current.email === values.email && current.password === values.password
                ? current
                : values
        ));
    }, [readFormValues]);

    useEffect(() => {
        const delays = [0, 100, 300, 800, 1500];
        const timers = delays.map(delay => window.setTimeout(syncAutofillValues, delay));
        autofillWatchRef.current = window.setInterval(syncAutofillValues, 250);

        autofillStopTimerRef.current = window.setTimeout(() => {
            if (autofillWatchRef.current) {
                window.clearInterval(autofillWatchRef.current);
                autofillWatchRef.current = null;
            }
        }, 4000);

        const handleWindowFocus = () => syncAutofillValues();
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                syncAutofillValues();
            }
        };

        window.addEventListener("focus", handleWindowFocus);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            timers.forEach(timer => window.clearTimeout(timer));
            if (autofillWatchRef.current) {
                window.clearInterval(autofillWatchRef.current);
                autofillWatchRef.current = null;
            }
            if (autofillStopTimerRef.current) {
                window.clearTimeout(autofillStopTimerRef.current);
                autofillStopTimerRef.current = null;
            }
            window.removeEventListener("focus", handleWindowFocus);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [syncAutofillValues]);

    const validateForm = (values = formData): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!values.email.trim()) {
            newErrors.email = "Email harus diisi";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
            newErrors.email = "Email tidak valid";
        }

        if (!values.password) {
            newErrors.password = "Password harus diisi";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error saat user mulai mengetik
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ""
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const submittedValues = readFormValues() || formData;
        setFormData(submittedValues);
        if (!validateForm(submittedValues)) return;

        setLoading(true);
        setApiError("");

        try {
            const response = await http.post("auth/login", submittedValues);

            console.log("Login successful:", response.data);

            const tokenObj = response.data.data.token;
            const tokenStr = typeof tokenObj === 'string' ? tokenObj : tokenObj.token;
            replaceAuthSession(tokenStr, response.data.data.user);
            dispatchStorageSync();

            navigate(requestedPath, { replace: true });
        } catch (error: any) {
            const errorMessage =
                error.response?.data?.message || "Login gagal. Silakan coba lagi.";
            setApiError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const isFormValid = Boolean(
        hasValidCredentials(formData) &&
        !Object.values(errors).some(err => err)
    );

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <h1>Selamat Datang Kembali</h1>
                    <p>Masuk ke akun Anda</p>
                </div>

                <form
                    ref={formRef}
                    onSubmit={handleSubmit}
                    onInput={syncAutofillValues}
                    onFocusCapture={() => window.requestAnimationFrame(syncAutofillValues)}
                    onAnimationStart={syncAutofillValues}
                    className="login-form"
                >
                    {apiError && (
                        <div className="alert alert-error">
                            {apiError}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            ref={emailInputRef}
                            value={formData.email}
                            onChange={handleChange}
                            onBlur={syncAutofillValues}
                            onAnimationStart={syncAutofillValues}
                            autoComplete="email"
                            placeholder="Masukkan email"
                            className={`form-input ${errors.email ? "error" : ""}`}
                        />
                        {errors.email && <span className="error-message">{errors.email}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password *</label>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            ref={passwordInputRef}
                            value={formData.password}
                            onChange={handleChange}
                            onBlur={syncAutofillValues}
                            onAnimationStart={syncAutofillValues}
                            autoComplete="current-password"
                            placeholder="Masukkan password"
                            className={`form-input ${errors.password ? "error" : ""}`}
                        />
                        {errors.password && <span className="error-message">{errors.password}</span>}
                    </div>

                    <button
                        type="submit"
                        className={`submit-button ${isFormValid && !loading ? "active" : "disabled"}`}
                        disabled={!isFormValid || loading}
                    >
                        {loading ? "Sedang Login..." : "Login"}
                    </button>

                    <div className="forgot-password-link">
                        <a href="/forgot-password">Forgot Password?</a>
                    </div>

                    <p className="register-link">
                        Belum punya akun?{" "}
                        <Link to="/register" state={{ from: requestedPath }}>
                            Daftar di sini
                        </Link>
                    </p>
                </form>
            </div>
        </div>
    );
};

export default Login;
